import { useMemo } from "react"

import * as reportsAPI from "@/api/useReports"
import { useGetScopes } from "@/api/useScopes"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { predicateHelpers } from "@/hooks/useHttp"
import { useQueryString } from "@/hooks/useQueryString"
import { getReportDefinition, getReportJob } from "./useReports.selector"

import type { ReportJobResponse } from "@/api/useReports.types"
import type { Config, Params, QueryClient } from "@/hooks/useHttp"

export const useWithScopes = <T, S = T>(
  hook: (params?: Params, config?: Config<T, S>) => any,
  params?: Params,
  config?: Config<T, S>
) => {
  const scopesCtx = useGetScopes()

  const context = hook(params, config)
  const { response, ...restContext } = context

  const nextResponse = useMemo(() => {
    if (!scopesCtx.response || !response) return response

    const { data: scopesData = [] } = scopesCtx.response
    const scopesMap = Object.fromEntries(
      scopesData.map((scope) => [scope.id, scope])
    )

    const { data = [], ...rest } = response

    return {
      data: data.map(
        ({
          isAllResources,
          ...report
        }: unknown & { isAllResources: boolean; scopes: string[] }) => {
          const reportScopes = report.scopes || []
          const currentIsAllResources =
            reportScopes.length === 1 &&
            scopesMap[reportScopes[0]]?.isAllResources

          return {
            isAllResources: currentIsAllResources,
            scopesDisplayValue: reportScopes
              .map((id) => scopesMap[id]?.name)
              .filter(Boolean)
              .join(", "),
            ...report
          }
        }
      ),
      ...rest
    }
  }, [response, scopesCtx.response])

  return { response: nextResponse, ...restContext }
}

export const useReportsDefinitions = (params?: Params) => {
  const scopesCtx = useGetScopes()

  const context = reportsAPI.useGetReportsDefinitions(params, {
    select: getReportDefinition
  })

  const { response: reportDefinitions, ...restContext } = context
  const { response: scopesResponse, ...restScopesContext } = scopesCtx

  const nextScopesResponse = useMemo(() => {
    if (!scopesResponse) return scopesResponse

    const { data = [], ...rest } = scopesResponse

    return {
      data: [
        data.filter(({ isAllResources }) => !isAllResources),
        data.find(({ isAllResources }) => !!isAllResources)
      ] as const,
      ...rest
    }
  }, [scopesResponse])

  const nextResponse = useMemo(() => {
    if (!scopesCtx.response || !reportDefinitions) return reportDefinitions

    const { data: scopesData = [] } = scopesCtx.response
    const scopesMap = Object.fromEntries(
      scopesData.map((scope) => [scope.id, scope])
    )

    const { data = [], ...rest } = reportDefinitions

    return {
      data: data.map(({ isAllResources, ...report }) => {
        const currentScopes = scopesMap[report.scopes?.[0]] || {}

        return {
          isAllResources: !!currentScopes.isAllResources,
          scopeName: currentScopes.name || "",
          ...report
        }
      }),
      ...rest
    }
  }, [reportDefinitions, scopesCtx.response])

  return [
    { response: nextResponse, ...restContext },
    { scopesCtx: { response: nextScopesResponse, ...restScopesContext } }
  ] as const
}

export const useReportsJobs = (config?: Config<ReportJobResponse>) => {
  const params = useQueryString()
  const context = useWithScopes<ReportJobResponse>(
    reportsAPI.useGetReportsJobs,
    { orderBy: "createdAt", asc: false, ...params },
    { select: getReportJob, ...config }
  )

  return [context, params] as const
}

const options = (queryClient: QueryClient) => ({
  onSettled: undefined,
  onSuccess: () =>
    queryClient.invalidateQueries({
      predicate: (query) =>
        predicateHelpers.includesKeysPredicate([
          RESOURCES_NAMES.REPORTS.DEFINITIONS
        ])(query) ||
        predicateHelpers.includesKeysPredicate([RESOURCES_NAMES.REPORTS.JOBS])(
          query
        )
    })
})

export const useCreateReports = () => {
  return reportsAPI.useCreateReports(undefined, undefined, options)
}

export const usePatchReports = () => {
  return reportsAPI.usePatchReports(undefined, undefined, options)
}
